import type { TestResult } from '@testflow/shared';
import { useTranslation } from 'react-i18next';
import { pickLang } from '../lib/pick-lang.js';

// Design-Spec §3.9 UI-Atome: StateChip (uppercase 10px), IdLabel (Roboto Mono, orange),
// E2EBadge, KiTranslatedBadge ("✦ KI-translated", verschwindet bei manueller Änderung —
// das Ausblenden übernimmt der Aufrufer anhand des jeweiligen `*_ai`-Flags).
// Farben ausschließlich über die Tokens aus src/styles/tokens/*.css (Tailwind-Klassen bzw.
// var(--token) für Tokens ohne Tailwind-Mapping) — kein Hex-Hardcoding.

// #112 (A6): `secondary` haengt der TestFlow-ID die Quell-ID aus der Importdatei an —
// `ETO-00042 · WMI_09`, optisch schwaecher. Die TestFlow-ID bleibt fuehrend: sie ist die ID, die
// TestFlow vergibt und die stabil bleibt; die Quell-ID ist nur die Bruecke zurueck in die Datei.
// Als Erweiterung DIESES Bausteins statt eigenem Markup an jeder Fundstelle — sonst driftet die
// Darstellung zwischen Katalogbaum, Testfall-Detail und Testplan-Auswahl auseinander.
// `secondary` ist ausdruecklich NUR lesbar: es gibt kein Bedienelement, um die Quell-ID zu
// aendern (bewusste Entscheidung, siehe anforderung.md A6 — Korrekturweg ist der Neuimport).
export function IdLabel({ id, secondary }: { id: string; secondary?: string | null }) {
  return (
    <span className="font-mono text-brand-accent">
      {id}
      {secondary ? <span className="text-text-muted"> · {secondary}</span> : null}
    </span>
  );
}

export function KiTranslatedBadge() {
  const { t } = useTranslation();
  return (
    <span
      title={t('catalog.aiTranslatedHint')}
      className="text-[10px] font-medium uppercase tracking-wide text-brand-accent"
    >
      ✦ {t('catalog.aiTranslated')}
    </span>
  );
}

// #81b: Blendet der Schritt-Sprachmodus (DE/EN/Beide) eine Sprache aus, darf ein `*_ai`-Abzeichen
// dieser Sprache nicht spurlos verschwinden — sonst versteckt der Umschalter genau die Warnung,
// fuer die #74 gebaut wurde. Bewusst ein EIGENES, kompakteres Zeichen statt KiTranslatedBadge
// wiederzuverwenden: der Hinweis sitzt an der #-Spalte, einem Feld, das selbst NICHT KI-uebersetzt
// ist — er meldet nur "irgendwo in dieser Zeile steckt ausgeblendeter KI-Text", keine Aussage
// ueber die #-Spalte selbst. Ein identisch aussehender Badge waere an dieser Stelle irrefuehrend.
export function HiddenAiTranslationHint({ language }: { language: 'de' | 'en' }) {
  const { t } = useTranslation();
  const label = t('catalog.aiTranslatedHiddenHint', { lang: language.toUpperCase() });
  return (
    // M2 (Review-Finding): ein <span> mit nur `title` ist per Tastatur/Touch unerreichbar, und
    // Screenreader lesen `title` auf einem <span> unzuverlässig vor. role="img" + aria-label
    // machen den Glyph (✦, hier ausschliesslich dekorativ — die eigentliche Information TRAEGT das
    // Label) als eigenstaendig ansagbares Element erreichbar. `title` bleibt zusaetzlich stehen,
    // fuer den Maus-Hover-Tooltip.
    <span
      role="img"
      aria-label={label}
      title={label}
      className="text-[10px] font-medium text-brand-accent"
    >
      ✦
    </span>
  );
}

// Task B3 (#53A): eigenes Abzeichen für "aus KI-Vorschlag übernommen" — bewusst NICHT
// KiTranslatedBadge zweckentfremdet, denn hier wurde nichts übersetzt, sondern aus einem vom
// Nutzer eingefügten SAP-Note-Text abgeleitet. Eigener Key (catalog.aiGenerated/-Hint), gleiches
// visuelles Muster (✦-Glyph, verschwindet bei manueller Änderung — das Ausblenden übernimmt der
// Aufrufer anhand des jeweiligen `*_ai`-Flags, wie bei KiTranslatedBadge).
// #204: `hint` erlaubt einen eigenen Tooltip (Defect-Titel); ohne bleibt es der Katalog-Hinweis.
export function KiGeneratedBadge({ hint }: { hint?: string } = {}) {
  const { t } = useTranslation();
  return (
    <span
      title={hint ?? t('catalog.aiGeneratedHint')}
      className="text-[10px] font-medium uppercase tracking-wide text-brand-accent"
    >
      ✦ {t('catalog.aiGenerated')}
    </span>
  );
}

// Task 10 (#113, A4): Das Abzeichen kann die Kette BEIM NAMEN nennen. Ohne `name` bleibt es das
// bisherige, namenlose „E2E" — die Testfall-Detailsicht und das Ausfuehrungsfeld zeigen mehrere
// Mitgliedschaften auf einmal und haben keinen einzelnen Namen zu nennen. Die Playlist-Karte hat
// ihn (ein Eintrag = eine Kette) und nennt ihn: „aus welcher Kette stammt dieser Fall" war bis
// hierher ueberhaupt nicht ablesbar (anforderung.md, Ausgangslage).
//
// `uppercase` gilt nur fuer das nackte Abzeichen — ein Kettenname in Grossbuchstaben („AUFTRAG BIS
// ZAHLUNG") liest sich wie ein Schild, nicht wie ein Name. `truncate` + `title`, weil die
// Playlist-Karte 360px breit ist und Kettennamen beliebig lang sein duerfen.
export function E2EBadge({ name }: { name?: string | null }) {
  const label = name ? `E2E · ${name}` : 'E2E';
  return (
    <span
      title={name ? label : undefined}
      className={`min-w-0 truncate rounded-chip border border-[var(--border)] px-1 py-0.5 text-[10px] font-medium tracking-wide text-text-muted ${name ? '' : 'uppercase'}`}
    >
      {label}
    </span>
  );
}

export function StateChip({
  label,
  tone = 'default',
}: {
  label: string;
  tone?: 'default' | 'ok' | 'warn' | 'fail';
}) {
  const toneClass =
    tone === 'ok'
      ? 'text-status-pass'
      : tone === 'warn'
        ? 'text-status-warn'
        : tone === 'fail'
          ? 'text-status-fail'
          : 'text-text-muted';
  return (
    <span className={`text-[10px] font-medium uppercase tracking-wide ${toneClass}`}>{label}</span>
  );
}

// #35: die Zuordnung Testergebnis → Ton/Label lebt genau hier. Scope-Tabelle,
// Fortschritts-Tabelle und Lauf-Historie zeigen dasselbe Ergebnis — driften die Zuordnungen
// auseinander, heißt derselbe Status an drei Stellen etwas anderes.
const RESULT_TONE: Record<TestResult | 'open', 'default' | 'ok' | 'warn' | 'fail'> = {
  successful: 'ok',
  error: 'fail',
  blocked: 'fail',
  repeat: 'warn',
  open: 'default',
};

export function ResultChip({ status, count }: { status: TestResult | 'open'; count?: number }) {
  const { t } = useTranslation();
  const label = t(`results.${status}`);
  return <StateChip tone={RESULT_TONE[status]} label={count ? `${label} ×${count}` : label} />;
}

export function ReadinessChip({
  value,
}: {
  value: 'bereit' | 'in_arbeit' | 'nicht_im_scope';
}) {
  const { t } = useTranslation();
  const tone = value === 'bereit' ? 'ok' : value === 'nicht_im_scope' ? 'warn' : 'default';
  return <StateChip label={t(`catalog.readiness.${value}`)} tone={tone} />;
}

// #188: Der Testfall-Titel steht als eigene Zeile unter dem Defect-Titel (Board, Tabelle,
// Seitenleiste, Meine Fehler) — der Defect-Titel allein sagt Keyusern nicht, zu welchem Testfall
// der Fehler gehört. Gekürzt wird im Code auf 80 Zeichen (… als 80. Zeichen), zusätzlich begrenzt
// `line-clamp-2` die Höhe auf der schmalen Kanban-Karte (kein `block` daneben: line-clamp setzt
// display:-webkit-box, `block` würde es überschreiben); der volle Titel steht im `title`.
// Anzeige per pickLang: die englische Oberfläche zeigt den englischen Titel (Rückfall Deutsch).
export const TEST_CASE_TITLE_MAX = 80;

export function shortenTitle(text: string, max: number = TEST_CASE_TITLE_MAX): string {
  return text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
}

export function TestCaseTitleLine({
  titleDe,
  titleEn,
}: {
  // `| undefined` ausdrücklich: exactOptionalPropertyTypes, Aufrufer reichen optionale Felder durch.
  titleDe?: string | null | undefined;
  titleEn?: string | null | undefined;
}) {
  const { i18n } = useTranslation();
  // Defensiv: Test-Mocks von react-i18next liefern teils kein `i18n`.
  const lang = i18n?.resolvedLanguage;
  const full = pickLang(lang, titleDe, titleEn).trim();
  if (!full) return null;
  return (
    <span
      data-testcase-title
      title={full}
      className="line-clamp-2 break-words text-[11px] text-text-muted"
    >
      {shortenTitle(full)}
    </span>
  );
}

// #126, #186, #192: die Antwort aus dem Fehler-Dialog („Testfall nicht bestanden“ / „Testfall
// bestanden, Fehler gemeldet“) als farbiger Chip — nur Anzeige, ohne Bedienelement (nicht änderbar:
// sie steht im Lauf, aus dem der Defect entstand). Als Baustein, weil Defect-Detail (Testleitung)
// und Nur-Lesen-Seitenleiste des Testers dasselbe zeigen. Fehlender Wert (PATCH-/POST-Antworten
// liefern ihn nicht) und null (Defect ohne Lauf) zählen gleich: kein Chip.
export function OutcomeChip({ stopsChain }: { stopsChain: boolean | null | undefined }) {
  const { t } = useTranslation();
  if (stopsChain === true) {
    return (
      <span
        data-outcome="failed"
        title={t('defect.stopsChain.failedHint')}
        className="rounded-chip border border-status-fail/40 bg-status-fail/10 px-2 py-0.5 text-xs font-medium text-status-fail"
      >
        {t('defect.stopsChain.failed')}
      </span>
    );
  }
  if (stopsChain === false) {
    return (
      <span
        data-outcome="passed"
        title={t('defect.stopsChain.passedHint')}
        className="rounded-chip border border-status-pass/40 bg-status-pass/10 px-2 py-0.5 text-xs font-medium text-status-pass"
      >
        {t('defect.stopsChain.passed')}
      </span>
    );
  }
  return null;
}
